import { createVNode, render, VNode, Component, ComponentPublicInstance, AppContext } from 'vue';
import InfoAlert from './InfoAlert.vue';
import ConfirmAlert from './ConfirmAlert.vue';
import ConfirmDelete from './ConfirmDelete.vue';
import { DeleteProps } from './utils';

// Define the component type for better type inference
type ConfirmComponent = Component & {
    new(): ComponentPublicInstance<{
        message: string;
        onConfirm: () => void;
        onCancel: () => void;
    }>;
};

type AlertComponent = Component & {
    new(): ComponentPublicInstance<{
        status: "success" | "danger" | "info" | "warning";
        message: string;
    }>;
};



/**
 * Displays a confirmation alert and returns a Promise that resolves to true/false.
 * @param message - The confirmation message.
 * @returns A Promise that resolves to true (confirm) or false (cancel).
 */
export function customAlert(appContext: AppContext | null, status: "success" | "danger" | "info" | "warning", message: string): void {
    
     // Create container and append to body
    const container: HTMLDivElement = document.createElement('div');
    document.body.appendChild(container);
    // 2. Define the cleanup and resolve function

    // 3. Create the VNode
    const vnode: VNode = createVNode((InfoAlert) as AlertComponent, {
        status,
        message,
    });

    if (appContext) {
        vnode.appContext = appContext;
    }

    // 4. Render the VNode into the container
    render(vnode, container);

    setTimeout(() => {
        // Clean up the DOM element after 3 seconds
        render(null, container);
        container.remove();
    }, 4000);
}


/**
 * Displays a confirmation alert and returns a Promise that resolves to true/false.
 * @param message - The confirmation message.
 * @returns A Promise that resolves to true (confirm) or false (cancel).
 */
export function customConfirm(appContext: AppContext | null, message: string): Promise<boolean> {
    
    // 1. Return the Promise immediately
    return new Promise<boolean>((resolve) => {
 
        // Create container and append to body
        const container: HTMLDivElement = document.createElement('div');
        document.body.appendChild(container);

        // 2. Define the cleanup and resolve function
        // The value parameter is typed as boolean as per the Promise definition
        const destroyAndResolve = (value: boolean): void => {
            // Clean up the DOM element
            render(null, container);
            container.remove();

            // Resolve the promise with the user's choice (true or false)
            resolve(value);
        };

        // 3. Create the VNode
        const vnode: VNode = createVNode((ConfirmAlert) as ConfirmComponent, {
            message,
            // When 'confirm' is emitted, destroy and resolve the promise with TRUE
            onConfirm: () => destroyAndResolve(true),
            // When 'cancel' is emitted, destroy and resolve the promise with FALSE
            onCancel: () => destroyAndResolve(false)
        });

        if (appContext) {
            vnode.appContext = appContext;
        }

        // 4. Render the VNode into the container
        render(vnode, container);
    });
}


/**
 * Displays a confirmation alert and returns a Promise that resolves to true/false.
 * @param message - The confirmation message.
 * @returns A Promise that resolves to true (confirm) or false (cancel).
 */
export function customConfirmDelete(appContext: AppContext | null, {
    message,
    title,
    warnText,
    confirm
}: DeleteProps): Promise<boolean> {
    // 1. Return the Promise immediately
    return new Promise<boolean>((resolve) => {

        // Create container and append to body
        const container: HTMLDivElement = document.createElement('div');
        document.body.appendChild(container);

        // 2. Define the cleanup and resolve function
        // The value parameter is typed as boolean as per the Promise definition
        const destroyAndResolve = (value: boolean): void => {
            // Clean up the DOM element
            render(null, container);
            container.remove();

            // Resolve the promise with the user's choice (true or false)
            resolve(value);
        };

        // 3. Create the VNode
        const vnode: VNode = createVNode(ConfirmDelete as ConfirmComponent, {
            message,
            title,
            warnText,
            confirm,
            // When 'confirm' is emitted, destroy and resolve the promise with TRUE
            onConfirm: () => destroyAndResolve(true),
            // When 'cancel' is emitted, destroy and resolve the promise with FALSE
            onCancel: () => destroyAndResolve(false)
        });
        if (appContext) {
            vnode.appContext = appContext;
        }

        // 4. Render the VNode into the container
        render(vnode, container);
    });
}

